import { useState, useCallback, useEffect } from 'react';

// keep all local storage keys in one place
export enum LocalStorageItemKeys {
  JSON_EDITOR_MODE = 'JSON_EDITOR_MODE',
  ORGANIZATION_TIME_RANGE = 'ORGANIZATION_TIME_RANGE',
  THEME = 'theme', // Lower case to keep the item backwards compatible
  HAS_DISMISSED_WELCOME_MESSAGE = 'HAS_DISMISSED_WELCOME_MESSAGE',
  PROCESS_WIZARD_DISPLAY_ADVANCED_SETTINGS = 'PROCESS_WIZARD_DISPLAY_ADVANCED_SETTINGS_',
  ONBOARDING_PROCESS = 'ONBOARDING_PROCESS',
  DISMISSED_BANNER_LIST = 'DISMISSED_BANNER_LIST',
}

export enum SessionStorageItemKeys {
  USER_LOGIN_INIT = 'USER_LOGIN_INIT',
}

type LocalKeyType = `${LocalStorageItemKeys}` | (string & {});

type SessionKeyType = `${SessionStorageItemKeys}` | (string & {});

type Deserializer = (value: string) => any;
type Serializer<T> = (value: T) => string;

interface StorageProps<T> {
  /**
   * Initial value that will be used if value in storage is not found
   */
  initialValue: T;

  /**
   * Should storage changes be synchronized between tabs
   */
  sync?: boolean;

  /**
   * Custom serializer for values
   * Useful when working with non-primitive types eg. Date's
   */
  serializer?: Serializer<T>;

  /**
   * Custom deserializer for values
   * Useful when working with non-primitive types eg. Date's
   */
  deserializer?: Deserializer;
}

interface LocalStorageProps<T> extends StorageProps<T> {
  /**
   * Storage entry key
   */
  key: LocalKeyType;
}

interface SessionStorageProps<T> extends StorageProps<T> {
  /**
   * Storage entry key
   */
  key: SessionKeyType;
}

type StorageType = 'localStorage' | 'sessionStorage';

const serializeJSON = <T>(value: T) => {
  try {
    return JSON.stringify(value);
  } catch (_error) {
    throw new Error('failed to serialize storage entry');
  }
};

const deserializeJSON = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const readInitialStorageValue = <T>(
  type: StorageType,
  key: string,
  deserializer: Deserializer,
  initialValue: T,
): T => {
  const storageValue = window[type].getItem(key);
  return storageValue !== null ? deserializer(storageValue) : initialValue;
};

const useStorage = <T>(
  type: StorageType,
  {
    key,
    initialValue,
    sync = false,
    serializer = serializeJSON,
    deserializer = deserializeJSON,
  }: LocalStorageProps<T> | SessionStorageProps<T>,
) => {
  const [value, setValue] = useState<T>(
    readInitialStorageValue<T>(type, key, deserializer, initialValue),
  );

  const syncListener = useCallback(
    (event: WindowEventMap['storage']) => {
      if (event.storageArea === window[type] && event.key === key) {
        setValue(typeof event.newValue === 'string' ? deserializer(event.newValue) : initialValue);
      }
    },
    [key, type],
  );

  /**
   * Sync storage entries across tabs if enabled
   */
  useEffect(() => {
    if (sync) {
      window.addEventListener('storage', syncListener);
    }
    return () => {
      if (sync) {
        window.removeEventListener('storage', syncListener);
      }
    };
  }, [sync, syncListener]);

  const setStorageValue = useCallback(
    (newValue: T | ((prevState: T) => T)) => {
      if (newValue instanceof Function) {
        return setValue((current) => {
          const result = newValue(current);
          window[type].setItem(key, serializer(result));
          return result;
        });
      }

      window[type].setItem(key, serializer(newValue));
      return setValue(newValue);
    },

    [key, type],
  );

  const removeStorageValue = useCallback(() => {
    window[type].removeItem(key);
    return setValue(initialValue);
  }, [key, type, initialValue]);

  return { storageValue: value, setStorageValue, removeStorageValue };
};

/**
 * Read, set or update local storage entry.
 * Changes will be synced between browser tabs if enabled.
 *
 * @example
 * const { storageValue, setStorageValue, removeStorageValue } = useLocalStorage({ key: 'foo', initialValue: 'bar' });
 */
export const useLocalStorage = <T = string>(props: LocalStorageProps<T>) =>
  useStorage<T>('localStorage', props);

/**
 * Read, set or update session storage entry.
 * Changes will be synced between browser tabs if enabled.
 *
 * @example
 * const { storageValue, setStorageValue, removeStorageValue } = useSessionStorage({ key: 'foo', initialValue: 'bar' });
 */
export const useSessionStorage = <T = string>(props: SessionStorageProps<T>) =>
  useStorage<T>('sessionStorage', props);
